import { AvatarDisplay } from "@repo/ui/components/avatar-display";
import type { BaseIconName } from "@repo/ui/components/icon";
import { Icon } from "@repo/ui/components/icon";
import { Kbd } from "@repo/ui/kbd";
import {
  useActions,
  useCommandPalette,
} from "@repo/ui/lib/actions/action-registry";
import { ShortcutKbd } from "@repo/ui/lib/actions/shortcut-kbd";
import type { Action, SubPageOption } from "@repo/ui/lib/actions/types";
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
  defaultFilter,
} from "@repo/ui/ui/command";
import { useMemo, useState } from "react";

/**
 * Token-gated command filter. Every whitespace-separated token in the search
 * must appear as a substring of the item's value or keywords before the item is
 * shown, then cmdk's fuzzy `defaultFilter` is used to rank the survivors. This
 * prevents fuzzy subsequence matching from surfacing unrelated items (e.g.
 * "COGS" matching "Change agent mode... SQL").
 */
const commandFilter = (value: string, search: string, keywords?: string[]) => {
  const haystack = `${value} ${keywords?.join(" ") ?? ""}`.toLowerCase();
  const tokens = search.toLowerCase().split(/\s+/).filter(Boolean);
  if (!tokens.every((token) => haystack.includes(token))) {
    return 0;
  }
  return defaultFilter?.(value, search, keywords) || 1;
};

/** Groups actions by their `group` field, preserving insertion order. */
const groupActions = (actions: Action[]) => {
  const groups = new Map<string, Action[]>();
  for (const action of actions) {
    const list = groups.get(action.group);
    if (list) {
      list.push(action);
    } else {
      groups.set(action.group, [action]);
    }
  }
  return groups;
};

/** Global command palette powered by the action registry. */
export const CommandPalette = () => {
  const actions = useActions();
  const palette = useCommandPalette();
  const [search, setSearch] = useState("");

  const grouped = useMemo(() => groupActions(actions), [actions]);

  const activeAction = useMemo(
    () =>
      palette.activePage
        ? actions.find((a) => a.id === palette.activePage)
        : null,
    [actions, palette.activePage],
  );

  const subPage = activeAction?.subPage;

  const handleOpenChange = (open: boolean) => {
    if (!open && palette.activePage) {
      palette.setActivePage(null);
      setSearch("");
      return;
    }
    palette.setOpen(open);
    if (!open) {
      palette.setActivePage(null);
      setSearch("");
    }
  };

  return (
    <CommandDialog open={palette.open} onOpenChange={handleOpenChange}>
      <Command filter={commandFilter}>
        <CommandInput
          value={search}
          onValueChange={setSearch}
          placeholder={subPage?.placeholder ?? "Search a command..."}
          icon={subPage?.icon as BaseIconName}
        />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          {subPage ? (
            <SubPageView
              subPage={subPage}
              onSelect={() => {
                palette.setActivePage(null);
                palette.setOpen(false);
                setSearch("");
              }}
            />
          ) : (
            <RootView
              grouped={grouped}
              search={search}
              onSelect={(action) => {
                if (action.subPage) {
                  palette.setActivePage(action.id);
                  setSearch("");
                } else {
                  action.perform();
                  palette.hide();
                  setSearch("");
                }
              }}
              onSelectOption={(option) => {
                option.perform();
                palette.hide();
                setSearch("");
              }}
            />
          )}
        </CommandList>
        <div className="flex items-center gap-3 border-border/50 border-t px-3 py-2 text-muted-foreground text-xs">
          <span className="flex items-center gap-1">
            Navigate <Kbd>&uarr;</Kbd> or <Kbd>&darr;</Kbd>
          </span>
          <span className="text-border">|</span>
          <span className="flex items-center gap-1">
            Select <Kbd>&crarr;</Kbd>
          </span>
          <span className="ml-auto flex items-center gap-1">
            Go back <Kbd>esc</Kbd>
          </span>
        </div>
      </Command>
    </CommandDialog>
  );
};

/** Renders the root action list grouped by category. */
const RootView = ({
  grouped,
  search,
  onSelect,
  onSelectOption,
}: {
  grouped: Map<string, Action[]>;
  search: string;
  onSelect: (action: Action) => void;
  onSelectOption: (option: SubPageOption) => void;
}) => (
  <>
    {[...grouped.entries()].map(([group, items]) => (
      <CommandGroup key={group} heading={group}>
        {items.map((action) => (
          <ActionItem
            key={action.id}
            action={action}
            search={search}
            onSelect={() => onSelect(action)}
            onSelectOption={onSelectOption}
          />
        ))}
      </CommandGroup>
    ))}
  </>
);

/** Renders a single action and, when searching, its flattened sub-page options. */
const ActionItem = ({
  action,
  search,
  onSelect,
  onSelectOption,
}: {
  action: Action;
  search: string;
  onSelect: () => void;
  onSelectOption: (option: SubPageOption) => void;
}) => (
  <>
    <CommandItem
      value={action.name}
      keywords={action.keywords}
      onSelect={onSelect}
    >
      {action.icon && (
        <Icon name={action.icon as BaseIconName} className="size-3.5" />
      )}
      <span>{action.name}</span>
      {action.shortcut && (
        <CommandShortcut>
          <ShortcutKbd shortcut={action.shortcut} />
        </CommandShortcut>
      )}
    </CommandItem>
    {search &&
      action.subPage?.options.map((option) => (
        <CommandItem
          key={option.id}
          value={`${option.name} ${option.id}`}
          keywords={[action.name, ...(option.keywords ?? [])]}
          data-checked={option.checked}
          onSelect={() => onSelectOption(option)}
        >
          {action.icon && (
            <Icon name={action.icon as BaseIconName} className="size-3.5" />
          )}
          <span className="text-muted-foreground">
            {action.name.replace(/\.{3}$/, "")}
          </span>
          <Icon name="chevron-right" className="size-3 text-muted-foreground" />
          <span>{option.name}</span>
        </CommandItem>
      ))}
  </>
);

/** Renders the options for a sub-page action. */
const SubPageView = ({
  subPage,
  onSelect,
}: {
  subPage: NonNullable<Action["subPage"]>;
  onSelect: () => void;
}) => (
  <CommandGroup>
    {subPage.options.map((option) => (
      <CommandItem
        key={option.id}
        value={`${option.name} ${option.id}`}
        keywords={option.keywords}
        data-checked={option.checked}
        onSelect={() => {
          option.perform();
          onSelect();
        }}
      >
        {option.avatar !== undefined ? (
          <AvatarDisplay
            avatar={option.avatar}
            fallbackIcon={(option.icon as BaseIconName) ?? "dataset"}
          />
        ) : (
          option.icon && (
            <Icon name={option.icon as BaseIconName} className="size-3.5" />
          )
        )}
        <span>{option.name}</span>
      </CommandItem>
    ))}
  </CommandGroup>
);
